import { useState } from "react"
import { useTip } from "@/components/Tooltip"
import { Karte, Leer, Segment } from "@/components/ui"
import { gruppiere, jahrLabel, kennwert, kwKurz, type AuswertungsDaten, type Filter } from "@/lib/auswertung"
import { cssVar, helligkeit, nf0, pf0, pf1 } from "@/lib/format"

interface Props {
  d: AuswertungsDaten
  tp: Float64Array
  ids: number[] // ohne NC-Typ-Filter
  f: Filter
}

const DIV = ["--div-n3", "--div-n2", "--div-n1", "--div-0", "--div-p1", "--div-p2", "--div-p3"]

/** Heat-Tabelle NC-Typ × Jahr: absolut (eine Farbe, hell → dunkel) oder Abweichung vom Mehrjahreswert. */
export function JahresVergleich({ d, tp, ids, f }: Props) {
  const tip = useTip()
  const [modus, setModus] = useState<"abs" | "dev">("abs")
  const jahre = [...f.jahre].sort((a, b) => a - b)
  const abs = modus === "abs"

  const zeilen = [...gruppiere(ids, (i) => d.rows.t[i])]
    .filter(([, g]) => g.length >= 5)
    .sort((a, b) => b[1].length - a[1].length)
    .map(([t, g]) => {
      const gesamt = kennwert(g.map((i) => tp[i]), f.kw) ?? 0
      const proJahr = gruppiere(g, (i) => d.rows.y[i])
      const zellen = jahre.map((y) => {
        const gg = proJahr.get(y) ?? []
        const v = gg.length >= 3 ? kennwert(gg.map((i) => tp[i]), f.kw) : null
        return { y, n: gg.length, v, dev: v == null ? null : (v / gesamt - 1) * 100 }
      })
      return { t, n: g.length, gesamt, zellen }
    })
  const vals = zeilen.flatMap((z) => z.zellen.filter((c) => c.v != null).map((c) => (abs ? (c.v as number) : (c.dev as number))))
  const sortiert = [...vals].sort((a, b) => a - b)
  const q = (p: number) => sortiert[Math.min(sortiert.length - 1, Math.max(0, Math.round(p * (sortiert.length - 1))))]
  const heat = [0, 1, 2, 3, 4, 5, 6].map((i) => cssVar(`--heat-${i}`))
  const div = DIV.map(cssVar)
  let lo = 0, hi = 1, cap = 10
  if (vals.length) {
    if (abs) { lo = q(0.05); hi = q(0.95); if (hi - lo < 1) { lo -= 50; hi += 50 } }
    else cap = Math.min(20, Math.max(4, q(0.95), -q(0.05)))
  }
  const fuellung = (v: number, dev: number) => {
    if (abs) return heat[Math.floor(Math.max(0, Math.min(0.999, (v - lo) / (hi - lo))) * 7)]
    const s = Math.max(-0.999, Math.min(0.999, dev / cap))
    return div[3 + Math.trunc(s * 3.5 + (s >= 0 ? 0.5 : -0.5))]
  }
  const th = "sticky top-0 bg-surface px-2 py-1.5 text-right text-[11.5px] font-medium text-muted"

  return (
    <Karte
      titel="Jahresvergleich je NC-Typ"
      unter="Zellen ab 3 Proben. Abweichung bezieht sich auf den Mehrjahreswert des NC-Typs in der aktuellen Auswahl."
      rechts={
        <Segment
          label="Darstellung"
          werte={[{ id: "abs", name: "kg/h" }, { id: "dev", name: "Abweichung %" }]}
          wert={modus}
          onChange={setModus}
        />
      }
    >
      {!ids.length ? <Leer /> : !vals.length ? <Leer>Zu wenige Proben je Jahr und NC-Typ.</Leer> : (
        <>
          <div className="min-w-0 overflow-x-auto">
            <table className="tabular w-full border-collapse text-[12.5px]">
              <thead>
                <tr>
                  <th className={`${th} text-left`}>NC-Typ</th>
                  <th className={th}>Proben</th>
                  {jahre.map((y) => <th key={y} className={th}>{jahrLabel(d, y)}</th>)}
                  <th className={th}>Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {zeilen.map((z) => (
                  <tr key={z.t} className={f.typ === z.t ? "bg-accent/10" : ""}>
                    <td className="border-b border-grid px-2 py-1.5 font-mono text-xs">{d.dims.typ[z.t]}</td>
                    <td className="border-b border-grid px-2 py-1.5 text-right text-muted">{nf0.format(z.n)}</td>
                    {z.zellen.map((c) => {
                      if (c.v == null) {
                        return (
                          <td key={c.y} title={`${c.n} Proben`} className="min-w-[48px] border-b-2 border-l-2 border-surface px-1.5 py-1.5 text-right text-muted">
                            {c.n ? `(${c.n})` : ""}
                          </td>
                        )
                      }
                      const bg = fuellung(c.v, c.dev as number)
                      const l = helligkeit(bg)
                      return (
                        <td
                          key={c.y}
                          className="min-w-[48px] border-b-2 border-l-2 border-surface px-1.5 py-1.5 text-right"
                          style={{ background: bg, color: l < 0.28 ? "#ffffff" : l > 0.45 ? "#101719" : undefined }}
                          onPointerMove={(e) => tip.zeige(e.clientX, e.clientY, `${d.dims.typ[z.t]} · ${c.y}`, [
                            { label: `Durchsatz (${kwKurz(f.kw)})`, wert: `${nf0.format(c.v as number)} kg/h` },
                            { label: "ggü. Mehrjahreswert", wert: `${pf1.format(c.dev as number)} %` },
                            { label: "Proben", wert: nf0.format(c.n) },
                          ])}
                          onPointerLeave={tip.verstecke}
                        >
                          {abs ? nf0.format(c.v) : `${pf0.format(c.dev as number)} %`}
                        </td>
                      )
                    })}
                    <td className="border-b border-grid px-2 py-1.5 text-right">{nf0.format(z.gesamt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-muted">
            <span>{abs ? `≤ ${nf0.format(lo)}` : `${pf0.format(-cap)} %`}</span>
            <span className="inline-flex gap-0.5">
              {(abs ? heat : div).map((c, i) => <i key={i} className="inline-block h-2.5 w-[22px] rounded-sm" style={{ background: c }} />)}
            </span>
            <span>{abs ? `≥ ${nf0.format(hi)} kg/h` : `${pf0.format(cap)} %`}</span>
            {!abs && <span>blau schneller, rot langsamer als der Mehrjahreswert</span>}
            <span>· (n) = weniger als 3 Proben</span>
          </div>
        </>
      )}
    </Karte>
  )
}
